import { SimCardStates } from '@racemap/sdk/schema/simCard';
import type { EnrichedTracker } from '@racemap/sdk/schema/trackers';
import { OneMinuteInMillis } from '@racemap/utilities/consts/time';
import { TRACKER_TYPES, TrackerActivationType } from '@racemap/utilities/consts/trackers';
import {
  getSimNetworkState,
  isTrackerOnline,
  isTrackerPoweredOff,
} from '@racemap/utilities/functions/trackers';
import type { Immutable } from 'immer';
import { DateTime } from 'luxon';

export enum Filters {
  Tags = 'tags',
  MyRole = 'myRole',
  Rentals = 'rentals',
  Events = 'events',
  Online = 'online',
  Network = 'network',
  SimCardStatus = 'simCardStatus',
  TrackerType = 'trackerType',
  Messages = 'messages',
  Location = 'location',
  Battery = 'battery',
  Activation = 'activation',
  Logistics = 'logistics',
  Users = 'users',
  LastDataAt = 'lastDataAt',
  LastConnectedAt = 'lastConnectedAt',
  LastDisconnectedAt = 'lastDisconnectedAt',
}

export enum MessageFilters {
  NO_NEW_MESSAGES = 'noNewMessages',
  WITH_ERROR_MESSAGES = 'errorCount',
  WITH_PENDING_MESSAGES = 'pendingCount',
  WITH_SCHEDULED_MESSAGES = 'scheduledCount',
}

export enum BatteryFilters {
  FromEmtpyTo15 = 'fromEmptyTo15',
  From15To25 = 'from15To25',
  From25To50 = 'from25To50',
  From50To75 = 'from50To75',
  From75To90 = 'from75To90',
  From90ToFull = 'from90ToFull',
}

export enum LocationFilters {
  NoLocation = 'NoLocation',
  Cell = 'Cell',
  NoFix = 'NoFix',
  Fix = 'Fix',
}

export enum LastAtFilters {
  Recently = 'recently',
  From1MinuteTo5Minutes = 'from1MinuteTo5Minutes',
  From5MinutesTo30Minutes = 'from5MinutesTo30Minutes',
  MoreThan30Minutes = 'moreThan30Minutes',
}

export enum OnlineFilters {
  Offline = 'Offline',
  Connected = 'Connected',
}

export enum NetworkFilters {
  Registered = 'Registered',
  Stale = 'Stale',
  NotRegistered = 'NotRegistered',
}

export enum ActivationFilters {
  ARCHIVED = 'archived',
  UNUSED = 'unused',
  USED = 'used',
  RACEMAPTRACKER = 'racemapTracker',
}

export enum LogisticsFilters {
  IN_STOCK = 'inStock',
  OUT_OF_STOCK = 'outOfStock',
}

export enum UserRoleFilters {
  OWNER = 'owner',
  EDITOR = 'editor',
  BORROWER = 'borrower',
}

export const isInBatteryFilterRange = (filter: BatteryFilters, value?: number): boolean => {
  if (value == null) return false;
  switch (filter) {
    case BatteryFilters.FromEmtpyTo15:
      return value <= 15;
    case BatteryFilters.From15To25:
      return value > 15 && value <= 25;
    case BatteryFilters.From25To50:
      return value > 25 && value <= 50;
    case BatteryFilters.From50To75:
      return value > 50 && value <= 75;
    case BatteryFilters.From75To90:
      return value > 75 && value <= 90;
    case BatteryFilters.From90ToFull:
      return value > 90;
    default:
      return false;
  }
};

export const getBatteryFilterCategory = (value: number): BatteryFilters => {
  if (value <= 15) {
    return BatteryFilters.FromEmtpyTo15;
  }
  if (value > 15 && value <= 25) {
    return BatteryFilters.From15To25;
  }
  if (value > 25 && value <= 50) {
    return BatteryFilters.From25To50;
  }
  if (value > 50 && value <= 75) {
    return BatteryFilters.From50To75;
  }
  if (value > 75 && value <= 90) {
    return BatteryFilters.From75To90;
  }
  return BatteryFilters.From90ToFull;
};

export const isInUpdatedAtTimeRange = (filter: LastAtFilters, datetime?: Date): boolean => {
  const duration = Date.now() - (datetime?.valueOf() || Number.NaN);
  if (Number.isNaN(duration)) return false;

  switch (filter) {
    case LastAtFilters.Recently:
      return duration <= OneMinuteInMillis;
    case LastAtFilters.From1MinuteTo5Minutes:
      return duration > OneMinuteInMillis && duration <= 5 * OneMinuteInMillis;
    case LastAtFilters.From5MinutesTo30Minutes:
      return duration > 5 * OneMinuteInMillis && duration <= 30 * OneMinuteInMillis;
    case LastAtFilters.MoreThan30Minutes:
      return duration > 30 * OneMinuteInMillis;
    default:
      return false;
  }
};

export const getLastAtFilter = (datetime?: Date): LastAtFilters => {
  const duration = Date.now() - (datetime?.valueOf() || Number.NaN);
  if (Number.isNaN(duration)) return LastAtFilters.MoreThan30Minutes;

  if (duration <= OneMinuteInMillis) {
    return LastAtFilters.Recently;
  }
  if (duration > OneMinuteInMillis && duration <= 5 * OneMinuteInMillis) {
    return LastAtFilters.From1MinuteTo5Minutes;
  }
  if (duration > 5 * OneMinuteInMillis && duration <= 30 * OneMinuteInMillis) {
    return LastAtFilters.From5MinutesTo30Minutes;
  }
  return LastAtFilters.MoreThan30Minutes;
};

export const getLocationState = (tracker: Immutable<EnrichedTracker>): LocationFilters => {
  const { lastTime, accuracy } = tracker.lastGeo;

  if (lastTime == null) return LocationFilters.NoLocation;

  if (accuracy == null || accuracy === -1) {
    return DateTime.fromJSDate(lastTime) < DateTime.now().minus({ minutes: 5 })
      ? LocationFilters.NoFix
      : LocationFilters.Fix;
  }
  if (accuracy === 0) return LocationFilters.NoFix;

  return LocationFilters.Fix;
};

export const getLocationRank = (locationState: LocationFilters): number => {
  return locationStateNumberMapping[locationState];
};

const locationStateNumberMapping: Record<LocationFilters, number> = {
  [LocationFilters.NoLocation]: 0,

  [LocationFilters.Cell]: 1,
  [LocationFilters.NoFix]: 2,
  [LocationFilters.Fix]: 3,
};

export const getSignalStrengthRank = (
  trackerState: EnrichedTracker['state'],
  isOnline: boolean,
): number => {
  if (!isOnline) return -1;
  if (trackerState.signalStrength == null) return 0;
  return trackerState.signalStrength;
};

export const getOnlineStateValue = (tracker: Immutable<EnrichedTracker>): OnlineFilters => {
  if (isTrackerOnline(tracker.state)) return OnlineFilters.Connected;
  return OnlineFilters.Offline;
};

export const getNetworkStateValue = (
  tracker: Immutable<EnrichedTracker>,
): NetworkFilters | null => {
  if (tracker.simCard == null) return null;
  const state = getSimNetworkState(tracker.simCard, tracker.state?.lastDataAt);
  if (state === 'connected') return NetworkFilters.Registered;
  if (state === 'stale') return NetworkFilters.Stale;
  return NetworkFilters.NotRegistered;
};

export const getSimCardStatusValue = (
  tracker: Immutable<EnrichedTracker>,
): SimCardStates | null => {
  return tracker.simCard?.status ?? null;
};

// Connected first, then the devices that were switched off deliberately, then the rest.
export const getOnlineStateRank = (trackerState: EnrichedTracker['state']): number => {
  if (isTrackerOnline(trackerState)) {
    return 3;
  }
  if (isTrackerPoweredOff(trackerState)) {
    return 1;
  }
  return 0;
};

export function getActivationState(trackerActivation: {
  type: TrackerActivationType;
  paidUntil?: Date | null | undefined;
}): ActivationFilters {
  switch (trackerActivation.type) {
    case TrackerActivationType.ARCHIVED:
      return ActivationFilters.ARCHIVED;
    case TrackerActivationType.RACEMAPTRACKER:
      return ActivationFilters.RACEMAPTRACKER;
    default:
      if (
        trackerActivation.paidUntil == null ||
        new Date(trackerActivation.paidUntil).getTime() < Date.now()
      ) {
        return ActivationFilters.UNUSED;
      }
      return ActivationFilters.USED;
  }
}

export function isUserInTrackerRole(
  userId: string,
  userRole: UserRoleFilters,
  tracker: Immutable<EnrichedTracker>,
): boolean {
  return (
    (userRole === UserRoleFilters.OWNER && tracker.owner.equals(userId)) ||
    (userRole === UserRoleFilters.EDITOR &&
      tracker.editors.some((editor) => editor.equals(userId))) ||
    (userRole === UserRoleFilters.BORROWER &&
      tracker.borrowers.some((borrower) => borrower.id.equals(userId)))
  );
}

export enum RentalDirection {
  OUT = 'out',
  IN = 'in',
}

export interface RentalGroup {
  direction: RentalDirection;
  counterpartyId: string;
  startTime: Date;
  endTime: Date;
}

/** `<direction>/<counterpartyId>/<startMs>-<endMs>` — epoch millis keep the key free of `/` and `:`. */
const rentalGroupKey = (group: RentalGroup): string =>
  `${group.direction}/${group.counterpartyId}/${group.startTime.valueOf()}-${group.endTime.valueOf()}`;

export const parseRentalGroupKey = (key: string): RentalGroup | null => {
  const [direction, counterpartyId, window] = key.split('/');
  if (direction !== RentalDirection.OUT && direction !== RentalDirection.IN) return null;
  if (counterpartyId == null || window == null) return null;

  const [start, end] = window.split('-').map(Number);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;

  return { direction, counterpartyId, startTime: new Date(start), endTime: new Date(end) };
};

/**
 * The rental groups a tracker puts you in. A rental is not an object — it is the borrower
 * entries on the devices — so both sides of a loan are derived here rather than stored.
 * Expired loans are dropped; upcoming ones are kept, because that is next week's packing list.
 */
export function getRentalGroupsOfTracker(
  tracker: Immutable<EnrichedTracker>,
  currentUserId: string | null,
  now: Date = new Date(),
): Array<string> {
  if (currentUserId == null) return [];

  const lendsOut =
    isUserInTrackerRole(currentUserId, UserRoleFilters.OWNER, tracker) ||
    isUserInTrackerRole(currentUserId, UserRoleFilters.EDITOR, tracker);

  const keys = new Set<string>();

  for (const borrower of tracker.borrowers) {
    const startTime = new Date(borrower.startTime);
    const endTime = new Date(borrower.endTime);
    if (endTime.valueOf() < now.valueOf()) continue;

    if (borrower.id.equals(currentUserId)) {
      keys.add(
        rentalGroupKey({
          direction: RentalDirection.IN,
          counterpartyId: tracker.owner.toHexString(),
          startTime,
          endTime,
        }),
      );
    } else if (lendsOut) {
      keys.add(
        rentalGroupKey({
          direction: RentalDirection.OUT,
          counterpartyId: borrower.id.toHexString(),
          startTime,
          endTime,
        }),
      );
    }
  }

  return Array.from(keys);
}

/**
 * Every live rental group, with the number of devices in each. Kept out of the filter-count
 * worker on purpose: the worker is optional (`window.Worker == null` leaves it null) and one
 * pass over the trackers costs far less than the JSON round trip it would ride on.
 */
export function getRentalGroupCounts(
  trackers: Iterable<Immutable<EnrichedTracker>>,
  currentUserId: string | null,
  now: Date = new Date(),
): Map<string, number> {
  const counts = new Map<string, number>();

  for (const tracker of trackers) {
    for (const group of getRentalGroupsOfTracker(tracker, currentUserId, now)) {
      counts.set(group, (counts.get(group) || 0) + 1);
    }
  }

  return counts;
}

enum TrackerMessageState {
  UNKNOWN = 'UNKNOWN',
  NO_NEW_MESSAGE = 'NO_NEW_MESSAGE',
  REVOKED = 'REVOKED',
  REQUESTED = 'REQUESTED',
  SCHEDULED = 'SCHEDULED',
  SENT = 'SENT',
  OK = 'OK',
  NOT_SENT = 'NOT_SENT',
  ERROR = 'ERROR',
}

const FilterMap = {
  [Filters.MyRole]: Array.from(Object.values(UserRoleFilters)),
  [Filters.Battery]: Array.from(Object.values(BatteryFilters)),
  [Filters.Location]: Array.from(Object.values(LocationFilters)),
  [Filters.Messages]: Array.from(Object.values(TrackerMessageState)),
  [Filters.Online]: Array.from(Object.values(OnlineFilters)),
  [Filters.Network]: Array.from(Object.values(NetworkFilters)),
  [Filters.SimCardStatus]: Array.from(Object.values(SimCardStates)),
  [Filters.TrackerType]: Array.from(Object.values(TRACKER_TYPES)),
  [Filters.Activation]: Array.from(Object.values(ActivationFilters)),
  [Filters.Logistics]: Array.from(Object.values(LogisticsFilters)),
  [Filters.LastDataAt]: Array.from(Object.values(LastAtFilters)),
  [Filters.LastConnectedAt]: Array.from(Object.values(LastAtFilters)),
  [Filters.LastDisconnectedAt]: Array.from(Object.values(LastAtFilters)),
  [Filters.Events]: [],
  [Filters.Tags]: [],
  [Filters.Users]: [],
  [Filters.Rentals]: [],
};

export function getEmptyFilterCounts(): Map<Filters, Map<string, number>> {
  return new Map(
    Object.values(Filters).map((f) => [f, new Map(FilterMap[f].map((fE) => [fE, 0]))]),
  );
}
